import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell, Users, Calendar, FileText, Shield,
  Landmark, Wallet, Target, Settings, Briefcase,
} from 'lucide-react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { SAUDI_HR_CONFIG, HR_CURRENCY_SUFFIX } from '../../shared/utils/hrSaudiDefaults';
import { DEFAULT_HR_CONFIG, normalizeHrConfig } from './hrConfigDefaults';
import { syncHrConfigToSettings } from './useHrConfig';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';

const DEFAULT_HR_CONFIG_LOCAL = DEFAULT_HR_CONFIG;

const HRConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.hrConfigPage.${key}`, opts);
  const { settings, patchSettings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [hrConfig, setHrConfig] = useState(DEFAULT_HR_CONFIG_LOCAL);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', label: p('tab_general'), icon: Settings },
    { id: 'compliance', label: p('tab_compliance'), icon: Landmark },
    { id: 'talent', label: p('tab_talent'), icon: Briefcase },
    { id: 'admin', label: p('tab_admin'), icon: Shield },
  ];

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/hr/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeHrConfig({
        ...(settings?.hr || {}),
        ...(raw && typeof raw === 'object' ? raw : {}),
      });
      setHrConfig(merged);
      syncHrConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading HR config:', error);
      const fallback = normalizeHrConfig(settings?.hr || {});
      setHrConfig(fallback);
      syncHrConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings, settings?.hr]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeHrConfig(hrConfig);
      await localApiAdapter.put('/hr/config', payload);
      syncHrConfigToSettings(payload, patchSettings, settingsRef.current);
      setHrConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving HR config:', error);
      alert(p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm(p('reset_confirm'))) {
      try {
        const resetPayload = normalizeHrConfig(DEFAULT_HR_CONFIG_LOCAL);
        setHrConfig(resetPayload);
        await localApiAdapter.put('/hr/config', resetPayload);
        syncHrConfigToSettings(resetPayload, patchSettings, settingsRef.current);
      } catch (error) {
        console.error('Error resetting HR config:', error);
        alert(p('save_error'));
      }
    }
  };

  const updateConfig = (key, value) => {
    setHrConfig((prev) => {
      const next = normalizeHrConfig({ ...prev, [key]: value });
      syncHrConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const CheckboxRow = ({ checked, onChange, label }) => (
    <div className="flex items-center">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 rounded border-white/20 bg-white/10 text-[var(--qawaem-forest,#2d5016)] focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
      />
      <label className="ml-3 text-white/80 text-right flex-1">{label}</label>
    </div>
  );

  const NumberField = ({ label, value, onChange, min, max, step = 1 }) => (
    <div>
      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
      />
    </div>
  );

  const tabContent = {
    general: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Users className="h-6 w-6" />
            {p('section_general')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_currency')}</label>
              <input
                type="text"
                value={`${HR_CURRENCY_SUFFIX} (SAR)`}
                readOnly
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/80 cursor-not-allowed"
              />
            </div>
            <NumberField
              label={p('label_working_hours')}
              value={hrConfig.workingHoursPerWeek}
              onChange={(v) => updateConfig('workingHoursPerWeek', v)}
              min={1}
              max={48}
            />
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_leave_accrual')}</label>
              <select
                value={hrConfig.annualLeaveAccrual}
                onChange={(e) => updateConfig('annualLeaveAccrual', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
              >
                <option value="monthly">{p('accrual_monthly')}</option>
                <option value="annual">{p('accrual_annual')}</option>
              </select>
            </div>
            <NumberField
              label={p('label_notice_period')}
              value={hrConfig.noticePeriod}
              onChange={(v) => updateConfig('noticePeriod', v)}
              min={1}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            {p('section_saudi_labor')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={p('label_probation_period')}
              value={hrConfig.probationPeriod}
              onChange={(v) => updateConfig('probationPeriod', v)}
              min={1}
              max={90}
            />
            <NumberField
              label={p('label_annual_leave_days')}
              value={hrConfig.annualLeaveDays}
              onChange={(v) => updateConfig('annualLeaveDays', v)}
              min={21}
            />
            <NumberField
              label={p('label_sick_leave_days')}
              value={hrConfig.sickLeaveDays}
              onChange={(v) => updateConfig('sickLeaveDays', v)}
              min={1}
              max={120}
            />
            <NumberField
              label={p('label_salary_alert_threshold')}
              value={hrConfig.salaryAlertThresholdSAR}
              onChange={(v) => updateConfig('salaryAlertThresholdSAR', v)}
              min={0}
              step={500}
            />
          </div>
        </div>
      </>
    ),

    compliance: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Landmark className="h-6 w-6" />
            {p('section_gosi')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow
              checked={hrConfig.gosiEnabled}
              onChange={(v) => updateConfig('gosiEnabled', v)}
              label={p('check_gosi_enabled')}
            />
            <CheckboxRow
              checked={hrConfig.eosbAccrualEnabled}
              onChange={(v) => updateConfig('eosbAccrualEnabled', v)}
              label={p('check_eosb_accrual')}
            />
            <NumberField
              label={p('label_gosi_saudi_employer')}
              value={hrConfig.gosiSaudiEmployerRate}
              onChange={(v) => updateConfig('gosiSaudiEmployerRate', v)}
              min={0}
              max={30}
              step={0.5}
            />
            <NumberField
              label={p('label_gosi_saudi_employee')}
              value={hrConfig.gosiSaudiEmployeeRate}
              onChange={(v) => updateConfig('gosiSaudiEmployeeRate', v)}
              min={0}
              max={30}
              step={0.5}
            />
            <NumberField
              label={p('label_gosi_expat_employer')}
              value={hrConfig.gosiExpatEmployerRate}
              onChange={(v) => updateConfig('gosiExpatEmployerRate', v)}
              min={0}
              max={10}
              step={0.5}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4">{p('section_saned')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow
              checked={hrConfig.sanedEnabled}
              onChange={(v) => updateConfig('sanedEnabled', v)}
              label={p('check_saned_enabled')}
            />
            <NumberField
              label={p('label_saned_employer')}
              value={hrConfig.sanedEmployerRate}
              onChange={(v) => updateConfig('sanedEmployerRate', v)}
              min={0}
              max={5}
              step={0.5}
            />
            <NumberField
              label={p('label_saned_employee')}
              value={hrConfig.sanedEmployeeRate}
              onChange={(v) => updateConfig('sanedEmployeeRate', v)}
              min={0}
              max={5}
              step={0.5}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Wallet className="h-6 w-6" />
            {p('section_wps')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow
              checked={hrConfig.wpsEnabled}
              onChange={(v) => updateConfig('wpsEnabled', v)}
              label={p('check_wps_enabled')}
            />
            <NumberField
              label={p('label_wps_deadline')}
              value={hrConfig.wpsDeadlineDay}
              onChange={(v) => updateConfig('wpsDeadlineDay', v)}
              min={1}
              max={31}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Target className="h-6 w-6" />
            {p('section_nitaqat')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow
              checked={hrConfig.nitaqatEnabled}
              onChange={(v) => updateConfig('nitaqatEnabled', v)}
              label={p('check_nitaqat_enabled')}
            />
            <NumberField
              label={p('label_nitaqat_target')}
              value={hrConfig.nitaqatTargetPct}
              onChange={(v) => updateConfig('nitaqatTargetPct', v)}
              min={0}
              max={100}
            />
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_nitaqat_activity')}</label>
              <input
                type="text"
                value={hrConfig.nitaqatActivityCode || ''}
                onChange={(e) => updateConfig('nitaqatActivityCode', e.target.value)}
                placeholder="مثال: 620101"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
              />
            </div>
          </div>
        </div>
      </>
    ),

    talent: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4">{p('section_recruitment')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow checked={hrConfig.autoPostJobs} onChange={(v) => updateConfig('autoPostJobs', v)} label={p('check_auto_post_jobs')} />
            <CheckboxRow checked={hrConfig.requireMultipleInterviews} onChange={(v) => updateConfig('requireMultipleInterviews', v)} label={p('check_multiple_interviews')} />
            <CheckboxRow checked={hrConfig.backgroundCheckRequired} onChange={(v) => updateConfig('backgroundCheckRequired', v)} label={p('check_background_check')} />
            <CheckboxRow checked={hrConfig.probationReviewMandatory} onChange={(v) => updateConfig('probationReviewMandatory', v)} label={p('check_probation_review')} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4">{p('section_training')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={p('label_mandatory_training_hours')}
              value={hrConfig.mandatoryTrainingHours}
              onChange={(v) => updateConfig('mandatoryTrainingHours', v)}
              min={0}
            />
            <NumberField
              label={p('label_alert_days_expiry')}
              value={hrConfig.alertDaysBeforeExpiry}
              onChange={(v) => updateConfig('alertDaysBeforeExpiry', v)}
              min={1}
            />
            <CheckboxRow checked={hrConfig.trackCertifications} onChange={(v) => updateConfig('trackCertifications', v)} label={p('check_track_certifications')} />
            <CheckboxRow checked={hrConfig.certificationExpiryAlerts} onChange={(v) => updateConfig('certificationExpiryAlerts', v)} label={p('check_certification_alerts')} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4">{p('section_performance')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_review_month')}</label>
              <select
                value={hrConfig.reviewMonth}
                onChange={(e) => updateConfig('reviewMonth', parseInt(e.target.value, 10))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <option key={month} value={month}>
                    {p(`month_${month}`)}
                  </option>
                ))}
              </select>
            </div>
            <CheckboxRow checked={hrConfig.annualReviewRequired} onChange={(v) => updateConfig('annualReviewRequired', v)} label={p('check_annual_review')} />
            <CheckboxRow checked={hrConfig.selfEvaluationEnabled} onChange={(v) => updateConfig('selfEvaluationEnabled', v)} label={p('check_self_evaluation')} />
            <CheckboxRow checked={hrConfig.managerEvaluationRequired} onChange={(v) => updateConfig('managerEvaluationRequired', v)} label={p('check_manager_evaluation')} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            {p('section_leave')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={p('label_max_consecutive_days')}
              value={hrConfig.maxConsecutiveLeaveDays}
              onChange={(v) => updateConfig('maxConsecutiveLeaveDays', v)}
              min={1}
            />
            <NumberField
              label={p('label_min_leave_notice')}
              value={hrConfig.minimumLeaveNotice}
              onChange={(v) => updateConfig('minimumLeaveNotice', v)}
              min={1}
            />
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_approval_levels')}</label>
              <select
                value={hrConfig.leaveApprovalLevels}
                onChange={(e) => updateConfig('leaveApprovalLevels', parseInt(e.target.value, 10))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
              >
                <option value="1">{p('approval_one_level')}</option>
                <option value="2">{p('approval_two_levels')}</option>
              </select>
            </div>
            <NumberField
              label={p('label_max_carry_forward')}
              value={hrConfig.maxCarryForwardDays}
              onChange={(v) => updateConfig('maxCarryForwardDays', v)}
              min={0}
            />
            <CheckboxRow checked={hrConfig.carryForwardLeave} onChange={(v) => updateConfig('carryForwardLeave', v)} label={p('check_carry_forward')} />
          </div>
        </div>
      </>
    ),

    admin: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Settings className="h-6 w-6" />
            {p('section_list_settings', { defaultValue: 'إعدادات القوائم والرموز' })}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={p('label_items_per_page', { defaultValue: 'عدد الصفوف في القائمة' })}
              value={hrConfig.itemsPerPage}
              onChange={(v) => updateConfig('itemsPerPage', v)}
              min={10}
              max={200}
            />
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_view', { defaultValue: 'العرض الافتراضي' })}</label>
              <select
                value={hrConfig.defaultView || 'table'}
                onChange={(e) => updateConfig('defaultView', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]"
              >
                <option value="table">{p('view_table', { defaultValue: 'جدول' })}</option>
                <option value="grid">{p('view_grid', { defaultValue: 'بطاقات' })}</option>
              </select>
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_employee_prefix', { defaultValue: 'بادئة رمز الموظف' })}</label>
              <input type="text" value={hrConfig.employeeReferencePrefix || 'EMP'} onChange={(e) => updateConfig('employeeReferencePrefix', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]" />
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_leave_prefix', { defaultValue: 'بادئة الإجازات' })}</label>
              <input type="text" value={hrConfig.leaveReferencePrefix || 'LEV'} onChange={(e) => updateConfig('leaveReferencePrefix', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]" />
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_contract_prefix', { defaultValue: 'بادئة العقود' })}</label>
              <input type="text" value={hrConfig.contractReferencePrefix || 'CON'} onChange={(e) => updateConfig('contractReferencePrefix', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]" />
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_sanction_prefix', { defaultValue: 'بادئة الجزاءات' })}</label>
              <input type="text" value={hrConfig.sanctionReferencePrefix || 'SAN'} onChange={(e) => updateConfig('sanctionReferencePrefix', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]" />
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_departure_prefix', { defaultValue: 'بادئة نهاية الخدمة' })}</label>
              <input type="text" value={hrConfig.departureReferencePrefix || 'DEP'} onChange={(e) => updateConfig('departureReferencePrefix', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]" />
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_evaluation_prefix', { defaultValue: 'بادئة التقييمات' })}</label>
              <input type="text" value={hrConfig.evaluationReferencePrefix || 'EVA'} onChange={(e) => updateConfig('evaluationReferencePrefix', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[var(--qawaem-bronze,#8b6914)]" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="h-6 w-6" />
            {p('section_documents')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={p('label_document_retention')}
              value={hrConfig.documentRetentionYears}
              onChange={(v) => updateConfig('documentRetentionYears', v)}
              min={1}
            />
            <CheckboxRow checked={hrConfig.contractTemplates} onChange={(v) => updateConfig('contractTemplates', v)} label={p('check_contract_templates')} />
            <CheckboxRow checked={hrConfig.autoGenerateContracts} onChange={(v) => updateConfig('autoGenerateContracts', v)} label={p('check_auto_generate_contracts')} />
            <CheckboxRow checked={hrConfig.digitalSignatures} onChange={(v) => updateConfig('digitalSignatures', v)} label={p('check_digital_signatures')} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Bell className="h-6 w-6" />
            {p('section_notifications')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow checked={hrConfig.notifyOnLeaveRequest} onChange={(v) => updateConfig('notifyOnLeaveRequest', v)} label={p('check_notify_leave')} />
            <CheckboxRow checked={hrConfig.notifyOnContractExpiry} onChange={(v) => updateConfig('notifyOnContractExpiry', v)} label={p('check_notify_contract_expiry')} />
            <CheckboxRow checked={hrConfig.notifyOnCertificateExpiry} onChange={(v) => updateConfig('notifyOnCertificateExpiry', v)} label={p('check_notify_certificate_expiry')} />
            <CheckboxRow checked={hrConfig.notifyOnIqamaExpiry} onChange={(v) => updateConfig('notifyOnIqamaExpiry', v)} label={p('check_notify_iqama')} />
            <CheckboxRow checked={hrConfig.notifyOnWorkPermitExpiry} onChange={(v) => updateConfig('notifyOnWorkPermitExpiry', v)} label={p('check_notify_work_permit')} />
            <CheckboxRow checked={hrConfig.notifyOnMedicalInsuranceExpiry} onChange={(v) => updateConfig('notifyOnMedicalInsuranceExpiry', v)} label={p('check_notify_medical')} />
            <CheckboxRow checked={hrConfig.notifyOnNitaqatBandChange} onChange={(v) => updateConfig('notifyOnNitaqatBandChange', v)} label={p('check_notify_nitaqat')} />
            <CheckboxRow checked={hrConfig.emailNotifications} onChange={(v) => updateConfig('emailNotifications', v)} label={p('check_email_notifications')} />
            <NumberField
              label={p('label_iqama_alert_days')}
              value={hrConfig.iqamaExpiryAlertDays}
              onChange={(v) => updateConfig('iqamaExpiryAlertDays', v)}
              min={1}
            />
            <NumberField
              label={p('label_work_permit_alert_days')}
              value={hrConfig.workPermitExpiryAlertDays}
              onChange={(v) => updateConfig('workPermitExpiryAlertDays', v)}
              min={1}
            />
            <NumberField
              label={p('label_medical_alert_days')}
              value={hrConfig.medicalInsuranceExpiryAlertDays}
              onChange={(v) => updateConfig('medicalInsuranceExpiryAlertDays', v)}
              min={1}
            />
            <NumberField
              label={p('label_contract_alert_days')}
              value={hrConfig.contractExpiryAlertDays}
              onChange={(v) => updateConfig('contractExpiryAlertDays', v)}
              min={1}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Shield className="h-6 w-6" />
            {p('section_security')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CheckboxRow checked={hrConfig.restrictDocumentAccess} onChange={(v) => updateConfig('restrictDocumentAccess', v)} label={p('check_restrict_access')} />
            <CheckboxRow checked={hrConfig.requireApprovalForChanges} onChange={(v) => updateConfig('requireApprovalForChanges', v)} label={p('check_require_approval')} />
            <CheckboxRow checked={hrConfig.auditTrail} onChange={(v) => updateConfig('auditTrail', v)} label={p('check_audit_trail')} />
          </div>
        </div>
      </>
    ),
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={tabs}
      tabContent={tabContent}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      loading={loading}
      saving={saving}
      saved={saved}
      savedMessage={p('saved')}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default HRConfigPage;
